import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../../i18n/I18nContext";
import { toast } from "../../ALERT/SystemToasts";
import { ask } from "../Provides/confirmBus";
import Button from "../UI/Button.jsx";
import { createLesson, deleteLesson, updateLesson } from "../../WebServer/services/sport/functionsSport";
import { DAY_NAMES, endOf, lessonDayOf, startOf, toHHMM } from "../../utils/lessonSchedule";
import { hhmmToMin } from "../../utils/sportSchedule";
import { validateLessonTimes } from "./sportForm";
import LessonRosterDialog from "./LessonRosterDialog";
import styles from "./Sport.module.css";
import useItemLock from "../../locks/useItemLock";
import LockedScreen from "../../locks/LockedScreen";
import ReadOnlyArea from "../../locks/ReadOnlyArea";
import SaveNotice from "../../locks/SaveNotice";
import useLiveRefresh from "../../locks/useLiveRefresh";
import { handleSaveError, withBaseVersion } from "../../locks/staleSave";
import { useLockRecovery } from "../../locks/LockLostBanner";

// The form has only a day and two times; the name is generated by the server.
function LessonForm({ lesson, onClose, onSaved }) {
  const { t } = useI18n();
  const editing = Boolean(lesson);
  const title = t(editing ? "sport.lessons.editTitle" : "sport.lessons.addTitle");
  const [day, setDay] = useState(lesson?.date?.day || lessonDayOf());
  const [start, setStart] = useState(lesson ? toHHMM(startOf(lesson)) : "16:00");
  const [end, setEnd] = useState(lesson ? toHHMM(endOf(lesson)) : "17:00");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const lockKey = editing ? `sportLesson:${lesson._id}` : null;
  const lock = useItemLock(lockKey);
  const readonly = lock.status === "readonly";
  // The latest version is the lesson the (live-refreshed) list passed in.
  const reloadLesson = () => {
    if (!lesson) return;
    setDay(lesson.date?.day || lessonDayOf());
    setStart(toHHMM(startOf(lesson)));
    setEnd(toHHMM(endOf(lesson)));
  };
  const recovery = useLockRecovery(lock, { reload: reloadLesson, draft: `${start} - ${end}` });

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (event) => {
    event.preventDefault();
    const invalid = validateLessonTimes(start, end);
    if (invalid) return setError(invalid);
    // No name and no roster are sent: the server keeps both, or regenerates a generated name.
    const payload = { date: { day: Number(day), startMin: hhmmToMin(start), endMin: hhmmToMin(end) } };
    setBusy(true);
    const res = editing ? await updateLesson(lesson._id, withBaseVersion(payload, lesson)) : await createLesson(payload);
    setBusy(false);
    if (!res.ok) {
      if (editing && handleSaveError(res, { draft: `${start} - ${end}`, setNotice, onReload: reloadLesson })) return;
      return setError(res.message);
    }
    toast.success(t("sport.saved"));
    onSaved();
  };

  return (
    <div className={styles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className={styles.dialog} role="dialog" aria-modal="true" aria-label={title} onSubmit={submit} noValidate>
        <div className={styles.dialogHeader}>
          <h2>{title}</h2>
          <Button type="button" size="sm" variant="ghost" onClick={onClose} aria-label={t("sport.close")}>✕</Button>
        </div>
        {recovery.element}
        {lock.status === "blocked" ? (
          <LockedScreen holder={lock.holder} />
        ) : lock.status === "loading" ? (
          <div role="status">جارٍ تحميل البيانات…</div>
        ) : (
        <ReadOnlyArea readonly={readonly}>
        <div className={styles.fields}>
          <label className={`${styles.field} ${styles.wide}`}>
            {t("sport.lessons.day")}
            <select value={day} onChange={(e) => setDay(Number(e.target.value))} autoFocus>
              {DAY_NAMES.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            {t("sport.lessons.start")}
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className={styles.field}>
            {t("sport.lessons.end")}
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
        </ReadOnlyArea>
        )}
        <SaveNotice notice={notice} />
        {error && <div className={styles.formError} role="alert">{error}</div>}
        <div className={styles.dialogActions}>
          <Button type="button" variant="secondary" onClick={onClose}>{t("sport.cancel")}</Button>
          {lock.status !== "blocked" && lock.status !== "loading" && <Button type="submit" loading={busy} disabled={readonly}>{t("sport.save")}</Button>}
        </div>
      </form>
    </div>
  );
}

export default function LessonsTab({ lessons, canManage, canCoach, reload }) {
  const { t, dir } = useI18n();
  const [form, setForm] = useState(null); // null | { lesson? }
  const [rosterId, setRosterId] = useState(null);
  useLiveRefresh(["sportLesson", "player", "sport"], reload);

  const sorted = useMemo(
    () => [...lessons].sort((a, b) => a.date.day - b.date.day || startOf(a) - startOf(b)),
    [lessons],
  );
  const roster = rosterId ? lessons.find((l) => l._id === rosterId) : null;

  const remove = async (lesson) => {
    const confirmed = await ask("delete", {
      title: t("sport.lessons.deleteTitle"), message: t("sport.lessons.deleteConfirm"), confirmText: t("sport.lessons.deleteOk"),
    }).catch(() => false);
    if (!confirmed) return;
    const res = await deleteLesson(lesson._id);
    if (!res.ok) return toast.warn(res.message);
    toast.success(t("sport.lessons.deleted"));
    reload();
  };

  const label = (key) => t(`sport.lessons.${key}`);

  return (
    <div dir={dir}>
      {canManage && (
        <div className={styles.lessonsBar}>
          <Button onClick={() => setForm({})}>{label("add")}</Button>
        </div>
      )}

      <div className={styles.tableWrap}>
        <table className={`table ${styles.cards}`}>
          <thead>
            <tr>
              <th>{label("day")}</th>
              <th>{label("start")}</th>
              <th>{label("end")}</th>
              <th>{label("players")}</th>
              <th>{t("sport.players.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length > 0 ? sorted.map((l) => (
              <tr key={l._id}>
                <td data-label={label("day")}><span className={styles.name}>{DAY_NAMES[l.date.day - 1]}</span></td>
                <td data-label={label("start")}>{toHHMM(startOf(l))}</td>
                <td data-label={label("end")}>{toHHMM(endOf(l))}</td>
                <td data-label={label("players")}>{(l.players || []).length}</td>
                <td data-label={t("sport.players.actions")}>
                  <div className={styles.rowActions}>
                    <Button size="sm" variant="secondary" onClick={() => setRosterId(l._id)}>{label("manage")}</Button>
                    {canManage && <Button size="sm" variant="warning" onClick={() => setForm({ lesson: l })}>{t("sport.edit")}</Button>}
                    {canManage && <Button size="sm" variant="danger" onClick={() => remove(l)}>{label("deleteOk")}</Button>}
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={5} className={styles.emptyState}>{label("empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <LessonForm
          lesson={form.lesson}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); reload(); }}
        />
      )}
      {roster && (
        <LessonRosterDialog lesson={roster} canEdit={canCoach} onClose={() => setRosterId(null)} reload={reload} />
      )}
    </div>
  );
}
